import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import CountryList from 'country-list';
import QueryString from 'query-string';
import { useQuery, useMutation } from 'react-query';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import { TryCall } from '@fastercise/common-frontend';
import { StripeWrap } from '@/core/stripe.middleware';
import formatPrice from '@/core/formatPrice';
import CheckoutService from '@/services/checkout.service';
import StripeService from '@/services/stripe.service';
import AuthService from '@/services/auth.service';
import { routeWithParams } from '@/core/router-helpers';
import ROUTES from '@/routes';

import { SCREEN } from './Checkout.constants';
import Checkout from './Checkout.component';

const readSession = ({ queryKey: [, sessionId] }) =>
  CheckoutService.read(sessionId);
const readTaxInfo = ({
  queryKey: [, country, postalCode, priceId, discount],
}) => StripeService.calculateTax({ country, postalCode, priceId, discount });

function CheckoutController({ location, history }) {
  const { sessionId } = QueryString.parse(location?.search || '');
  const elements = useElements();
  const stripe = useStripe();

  const [currentScreen] = useState(SCREEN.ACCOUNT_DETAILS);
  const [errors, setErrors] = useState([]);
  const [isCheckingAccountDetails, setIsCheckingAccountDetails] = useState(
    false
  );
  const [accountDetails, setAccountDetails] = useState();

  const formCountry = accountDetails?.country?.code;
  const formPostalCode = accountDetails?.postalCode;

  const loginMutation = useMutation((credentials) =>
    AuthService.login(credentials.email, credentials.password)
  );

  const {
    isLoading: isSessionLoading,
    data: session,
    error: sessionError,
  } = useQuery(['CheckoutSession.read()', sessionId], readSession, {
    enabled: !!sessionId,
  });

  const { isLoading: isTaxInfoLoading, data: taxInfo } = useQuery(
    [
      'StripeService.calculateTax',
      session?.country || formCountry,
      session?.postalCode || formPostalCode,
      session?.priceId,
      session?.affiliatePlan?.affiliateMemberDiscount
        ? session?.affiliatePlan?.id
        : undefined,
    ],
    readTaxInfo,
    {
      enabled: Boolean(session?.country || formCountry),
      throwOnError: false,
      retry: false,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (session?.complete) {
      history.replace(routeWithParams(ROUTES.CHECKOUT_COMPLETE));
    }
  }, [session]);

  useEffect(() => {
    if (sessionError) {
      history.replace(routeWithParams(ROUTES.SELECT_PLAN));
    }
  }, [sessionError]);

  const handleErrorsClose = () => {
    setErrors([]);
  };
  const handleGoBackPress = useCallback(
    () =>
      history.replace(
        routeWithParams(ROUTES.SELECT_PLAN, {
          query: { affiliateCode: session?.affiliateCode, sessionId },
        })
      ),
    [session]
  );

  async function handleAddCard(values) {
    try {
      const card = elements.getElement(CardElement);
      const {
        paymentMethod,
        error: paymentMethodError,
      } = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: {
          name: `${values.firstName} ${values.lastName}`,
        },
      });

      if (paymentMethodError) {
        console.log(paymentMethodError);
        setErrors([paymentMethodError.message]);
        setIsCheckingAccountDetails(false);
        return null;
      }

      const paymentMethodId = paymentMethod?.id;

      if (!paymentMethodId) {
        setErrors(['Failed to create payment method']);
        setIsCheckingAccountDetails(false);
        return null;
      }
      return paymentMethodId;
    } catch (error) {
      console.log(error);
    }
  }

  const handleAccountDetailsChange = ({ values }) => setAccountDetails(values);
  const handleAccountDetailsSubmit = async ({ values, validationState }) => {
    if (!validationState.valid) {
      return;
    }

    setIsCheckingAccountDetails(true);

    let paymentMethodId = null;

    console.log(session.affiliatePlan?.affiliateMemberExempt);
    if (!session.affiliatePlan?.affiliateMemberExempt) {
      paymentMethodId = await handleAddCard(values);
    }

    if (!session.userId) {
      const {
        firstName,
        lastName,
        email,
        country: { code: country },
        postalCode,
        password,
        phone,
      } = values;

      const { err: userError } = await TryCall(
        AuthService.createAccount({
          sessionId,
          firstName,
          lastName,
          email,
          country,
          postalCode,
          password,
          phone,
          priceId: session.priceId,
          affiliateCode: session.affiliateCode,
          ...(paymentMethodId
            ? { stripePaymentMethodId: paymentMethodId }
            : {}),
        })
      );

      if (userError) {
        setErrors([userError.message]);
        setIsCheckingAccountDetails(false);
        return;
      }

      await loginMutation.mutateAsync({ email, password });

      history.replace(routeWithParams(ROUTES.ACCOUNT));
    }
  };

  const getCountries = () => sortBy(CountryList.getData(), ['name']);
  const getCountryLabel = ({ name }) => name;
  const getCountrySelected = ({ code: optionId }, { code: valueId }) =>
    optionId === valueId;

  const tax = (taxInfo?.tax || 0) / 100;
  const total = (taxInfo?.total || 0) / 100;
  const currency = get(session, 'price.currency', 'usd');
  const trialDuration =
    get(session, 'affiliatePlan.affiliateMemberTrial') ||
    get(session, 'product.trialDuration');

  const controller = {
    errors,
    session,
    currentScreen,
    product: session && {
      name: get(session, 'product.name'),
      price: get(session, 'price.unit_amount', 0) / 100,
      interval: get(session, 'price.recurring.interval_count'),
      currency: get(session, 'price.currency'),
      trialDuration: get(session, 'product.trialDuration'),
    },
    tax: taxInfo?.tax !== undefined && formatPrice(tax, currency),
    total: total && formatPrice(total, currency),
    trialDuration,
    currency,
    affiliatePlan: session?.affiliatePlan,
    isCheckingAccountDetails,
    isSessionLoading,
    isTaxInfoLoading,
    getCountries,
    getCountryLabel,
    getCountrySelected,
    handleAccountDetailsChange,
    handleAccountDetailsSubmit,
    handleErrorsClose,
    handleGoBackPress,
    showCard: !session?.affiliatePlan?.affiliateMemberExempt,
  };

  return <Checkout controller={controller} />;
}

export default function CheckoutContainer({ location, history }) {
  return (
    <StripeWrap>
      <CheckoutController location={location} history={history} />
    </StripeWrap>
  );
}

CheckoutContainer.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
};

CheckoutController.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
};
